import { ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Input, Modal, Select, Space, Table, Tag, Tooltip, Typography, message } from "antd";
import {
    MAX_RETIRE_SUBSUMED,
    REVIEW_NOTE_MAX_LENGTH,
    SUBSUMED_REASON_CODES,
    retireOutcomeSummary,
    reviewReasonCodeLabel,
    subsumedStatusColor,
    subsumedStatusLabel,
} from "common/reviewQueue";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "hooks";
import React from "react";
import { clearSubsumedActionResult, getSubsumedRecordsRequest, restoreSubsumedRequest, retireSubsumedRequest } from "store/reviewQueue/actions";

interface ISubsumedRecordsProps {
    businessAreaId?: string;
    schemaId?: string;
    // Whether the user is a steward (goldenstewardship review): only stewards retire and restore.
    canReview: boolean;
}

const formatDate = (value?: string | null) => (value ? dayjs(value).format("DD MMM YYYY HH:mm") : "");

const STATUS_FILTERS = [
    { label: "All", value: "" },
    { label: "Flagged as subsumed", value: "SUBSUMED" },
    { label: "Retired", value: "RETIRED" },
];

// What a steward is asked for: retiring the selected flagged records, or restoring one.
type PendingAction = { action: "retire"; dataObjectIds: string[] } | { action: "restore"; record: ISubsumedRecord };

// PRE-2837 — the review queue's "Subsumed records": the schema's duplicates that deduplication
// flagged as subsumed by a survivor in their own dataset (still live, left out of later duplicate
// searches) or retired into it. Stewards retire flagged ones, or restore one (un-merge).
const SubsumedRecords = ({ businessAreaId, schemaId, canReview }: ISubsumedRecordsProps) => {
    const dispatch = useAppDispatch();
    const { subsumed, subsumedPageKey, loadingSubsumed, getSubsumedResult, actingOnSubsumed, subsumedActionResult } = useAppSelector((state) => state.reviewQueue);
    const [status, setStatus] = React.useState("");
    const [selected, setSelected] = React.useState<string[]>([]);
    const [pending, setPending] = React.useState<PendingAction | null>(null);
    const [reasonCode, setReasonCode] = React.useState<string | undefined>();
    const [note, setNote] = React.useState("");
    const [actionError, setActionError] = React.useState<string | null>(null);
    // Why the last retire left some records as they were.
    const [notRetired, setNotRetired] = React.useState<string[]>([]);

    const refresh = React.useCallback(() => {
        if (businessAreaId && schemaId) {
            dispatch(getSubsumedRecordsRequest(businessAreaId, schemaId, status ? { status } : {}));
        }
    }, [dispatch, businessAreaId, schemaId, status]);

    React.useEffect(() => {
        setSelected([]);
        refresh();
    }, [refresh]);

    // Only records still listed stay selected (one restored, or gone on a refresh, does not).
    React.useEffect(() => {
        setSelected((current) => current.filter((dataObjectId) => subsumed.some((entry) => entry.dataObjectId === dataObjectId)));
    }, [subsumed]);

    const close = () => {
        setPending(null);
        setReasonCode(undefined);
        setNote("");
        setActionError(null);
    };

    // Shown once: a success closes the dialog (a retire says what became of each record); a
    // failure stays in it.
    React.useEffect(() => {
        if (!subsumedActionResult) {
            return;
        }
        // Another schema's outcome (it answered after the switch) is not this view's to show.
        if (subsumedActionResult.schemaId && subsumedActionResult.schemaId !== schemaId) {
            dispatch(clearSubsumedActionResult());
            return;
        }
        if (subsumedActionResult.success) {
            if (subsumedActionResult.action === "retire") {
                const summary = retireOutcomeSummary(subsumedActionResult);
                (summary.type === "success" ? message.success : message.warning)(summary.text);
                setNotRetired(summary.notRetired);
                setSelected([]);
                refresh();
            } else {
                message.success("Record restored. It is matched again by later duplicate searches, and no longer flagged into that survivor.");
                setNotRetired([]);
            }
            close();
        } else {
            setActionError(subsumedActionResult.message || "The action could not be completed.");
            // Shown as it now is (a record that cannot be restored may no longer be listed).
            refresh();
        }
        dispatch(clearSubsumedActionResult());
    }, [dispatch, subsumedActionResult, refresh, schemaId]);

    const noteNeeded = reasonCode === "OTHER" && !note.trim();
    const submit = () => {
        if (!pending || !reasonCode || noteNeeded || !businessAreaId || !schemaId) {
            return;
        }
        setActionError(null);
        const reason = { reasonCode, ...(note.trim() ? { note: note.trim() } : {}) };
        if (pending.action === "retire") {
            dispatch(retireSubsumedRequest(businessAreaId, schemaId, { dataObjectIds: pending.dataObjectIds, ...reason }));
        } else {
            dispatch(restoreSubsumedRequest(businessAreaId, schemaId, pending.record.dataObjectId, reason));
        }
    };

    const columns = [
        {
            title: "Record",
            dataIndex: "dataObjectId",
            key: "dataObjectId",
            render: (dataObjectId: string, row: ISubsumedRecord) =>
                row.redacted ? <Typography.Text type="secondary">In another business area</Typography.Text> : <Typography.Text copyable>{dataObjectId}</Typography.Text>,
        },
        { title: "Survivor", dataIndex: "survivorId", key: "survivorId", render: (survivorId: string) => survivorId && <Typography.Text copyable>{survivorId}</Typography.Text> },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (value: string) => value && <Tag color={subsumedStatusColor(value)}>{subsumedStatusLabel(value)}</Tag>,
        },
        { title: "Search run", dataIndex: "setVersion", key: "setVersion" },
        { title: "Flagged", dataIndex: "flaggedDate", key: "flaggedDate", render: (value: string) => formatDate(value) },
        { title: "Retired", dataIndex: "retiredDate", key: "retiredDate", render: (value: string) => formatDate(value) },
        { title: "Reason", dataIndex: "reasonCode", key: "reasonCode", render: (value: string) => reviewReasonCodeLabel(value) },
        {
            key: "restore",
            render: (_: unknown, row: ISubsumedRecord) =>
                canReview && !row.redacted ? (
                    <Tooltip title="Un-merge: unflag it, or bring it back if retired. No run flags it into this survivor again.">
                        <Button type="link" onClick={() => setPending({ action: "restore", record: row })} data-testid="subsumed-restore-button">
                            Restore
                        </Button>
                    </Tooltip>
                ) : null,
        },
    ];

    const tooMany = selected.length > MAX_RETIRE_SUBSUMED;

    return (
        <div className="flex flex-col gap-3" data-testid="subsumed-records">
            <Typography.Paragraph type="secondary" className="!mb-0">
                Duplicates in the same dataset as their survivor, merged into it by deduplication. A record flagged as subsumed stays in its dataset and is left out of later
                duplicate searches until it is retired (removed from its dataset, remembering its survivor) or restored. Records sent for review instead are in the review items.
            </Typography.Paragraph>
            <Space className="w-full justify-between" wrap>
                <Select value={status} style={{ width: 200 }} options={STATUS_FILTERS} onChange={setStatus} data-testid="subsumed-status-filter" />
                <Space wrap>
                    {canReview && (
                        <Tooltip title={tooMany ? `Retire at most ${MAX_RETIRE_SUBSUMED} at a time.` : undefined}>
                            <Button
                                type="primary"
                                danger
                                disabled={selected.length === 0 || tooMany}
                                onClick={() => setPending({ action: "retire", dataObjectIds: selected })}
                                data-testid="subsumed-retire-button"
                            >
                                Retire flagged ({selected.length})
                            </Button>
                        </Tooltip>
                    )}
                    <Button icon={<ReloadOutlined />} onClick={refresh} data-testid="subsumed-refresh-button">
                        Refresh
                    </Button>
                </Space>
            </Space>

            {!getSubsumedResult.success && (
                <Alert type="error" showIcon data-testid="subsumed-error" title={getSubsumedResult.message || "The subsumed records could not be loaded."} />
            )}
            {notRetired.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    closable
                    onClose={() => setNotRetired([])}
                    data-testid="subsumed-not-retired"
                    title="Some records were not retired"
                    description={
                        <ul className="!mb-0">
                            {notRetired.map((line) => (
                                <li key={line}>{line}</li>
                            ))}
                        </ul>
                    }
                />
            )}

            <Table
                size="small"
                rowKey={(row: ISubsumedRecord) => row.dataObjectId || `hidden-${subsumed.indexOf(row)}`}
                loading={loadingSubsumed}
                dataSource={subsumed}
                columns={columns}
                pagination={false}
                rowSelection={
                    canReview
                        ? {
                              selectedRowKeys: selected,
                              onChange: (keys) => setSelected(keys as string[]),
                              // Only flagged records of this business area can be retired.
                              getCheckboxProps: (row: ISubsumedRecord) => ({ disabled: !!row.redacted || row.status !== "SUBSUMED" }),
                          }
                        : undefined
                }
                locale={{ emptyText: "No subsumed records for this schema." }}
                data-testid="subsumed-table"
            />
            {subsumedPageKey && (
                <Button
                    onClick={() => businessAreaId && schemaId && dispatch(getSubsumedRecordsRequest(businessAreaId, schemaId, status ? { status } : {}, subsumedPageKey, true))}
                    loading={loadingSubsumed}
                    data-testid="subsumed-load-more-button"
                >
                    Load more
                </Button>
            )}

            {pending && (
                <Modal
                    open
                    title={pending.action === "retire" ? `Retire ${pending.dataObjectIds.length} flagged record(s)` : "Restore this record"}
                    okText={pending.action === "retire" ? "Retire" : "Restore"}
                    okButtonProps={{ disabled: !reasonCode || noteNeeded, danger: pending.action === "retire", "data-testid": "subsumed-action-submit-button" } as any}
                    confirmLoading={actingOnSubsumed}
                    onOk={submit}
                    onCancel={close}
                    destroyOnHidden
                    data-testid="subsumed-action-modal"
                >
                    <div className="flex flex-col gap-3">
                        <Typography.Paragraph type="secondary" className="!mb-0">
                            {pending.action === "retire"
                                ? "Each record is removed from its dataset and remembers its survivor; it can be restored later. A record edited since it was flagged, or that has become the survivor of another search's cluster, is left as it is."
                                : pending.record.status === "RETIRED"
                                  ? "The record comes back to its dataset with its values as they were, unless another record has taken its primary key meanwhile. It no longer counts as a source of the golden record, and no run flags it into this survivor again."
                                  : "The record is no longer flagged: later duplicate searches match it again, but no run flags it into this survivor again."}
                        </Typography.Paragraph>
                        {actionError && <Alert type="error" showIcon title={actionError} data-testid="subsumed-action-error" />}
                        <div>
                            <Typography.Text strong>Reason</Typography.Text>
                            <Select
                                className="w-full"
                                placeholder="Why?"
                                value={reasonCode}
                                options={SUBSUMED_REASON_CODES[pending.action].map((code) => ({ label: reviewReasonCodeLabel(code), value: code }))}
                                onChange={setReasonCode}
                                data-testid="subsumed-action-reason"
                            />
                        </div>
                        <div>
                            <Typography.Text strong>Note{reasonCode === "OTHER" ? "" : " (optional)"}</Typography.Text>
                            <Input.TextArea
                                rows={3}
                                maxLength={REVIEW_NOTE_MAX_LENGTH}
                                showCount
                                value={note}
                                onChange={(event) => setNote(event.target.value)}
                                status={noteNeeded ? "error" : undefined}
                                data-testid="subsumed-action-note"
                            />
                            {noteNeeded && <Typography.Text type="danger">Say why, when the reason is Other.</Typography.Text>}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default SubsumedRecords;
